/// <amd-module name="TFS/Security/Common/SecurityPageView" />
import * as React from "react";

import { createPermissionReport, getSecurityViewData } from "DataSource/SecurityRootViewDataSource";
import { ResourceSecurityComponent } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { Feature, PerformanceScenarios } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { PermissionsContext } from "TFS/Admin/Common/Contracts/Permissions";
import { createMarkup } from "TFS/Admin/Common/Util/HtmlString";
import { sendToast } from "TFS/Admin/Common/Util/SendToastUtility";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { IMessageCardContent, ISecurityViewData } from "TFS/Security/Common/Contracts";
import * as Resources from "TFS/Security/Common/Resources";
import { ISecurityPageViewProps } from "TFS/Security/Common/Security.Controls.Props";
import { logTelemetry } from "Util";
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { WrappedComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import * as Constants from "TFS/Security/Common/Constants";
import { Header } from "VSSUI/Header";

export class SecurityPageView extends VssComponent<ISecurityPageViewProps> {
    private _performanceService: IVssPerformanceService;
    private _permissionsContext: PermissionsContext | undefined = undefined;
    private data: ISecurityViewData | undefined;
    private _initialLoadComplete: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _isExportPermissionsDisabled: IObservableValue<boolean> = new ObservableValue<boolean>(false);
    private _messageCardInfo: IObservableValue<IMessageCardContent | undefined> = new ObservableValue<IMessageCardContent | undefined>(undefined);
    private _user: IdentityRef | undefined = undefined;

    constructor(props: ISecurityPageViewProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const vssPageData = vssPageService.getData();
        if (vssPageData) {
            this._user = vssPageData.user;
        }

        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this._getSecurityViewData();
    }

    public render(): JSX.Element {
        return (
            <div className="flex-column security-root">
                <Observer messageCardInfo={this._messageCardInfo}>
                    {(props: { messageCardInfo: IMessageCardContent | undefined }) =>
                        props.messageCardInfo !== undefined && (
                            <MessageCard
                                className="message-card"
                                severity={props.messageCardInfo.severity}
                                onDismiss={() => {
                                    this._messageCardInfo.value = undefined;
                                }}
                            >
                                <span dangerouslySetInnerHTML={createMarkup(props.messageCardInfo.message!)} />
                            </MessageCard>
                        )
                    }
                </Observer>
                <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <div className="security-page-view flex-column">
                        <div className="flex">
                            <Header title={this.props.securityViewTitle ?? ""} />
                            <Observer initialLoadComplete={this._initialLoadComplete} isExportPermissionsDisabled={this._isExportPermissionsDisabled}>
                                {(props: { initialLoadComplete: boolean; isExportPermissionsDisabled: boolean }) =>
                                    props.initialLoadComplete &&
                                    this._canDownloadReport() && (
                                        <Button
                                            className="report-btn"
                                            onClick={this._onDownloadReportClick}
                                            disabled={props.isExportPermissionsDisabled}
                                        >
                                            {Resources.DownloadReportBtn}
                                        </Button>
                                    )
                                }
                            </Observer>
                        </div>

                        <Observer initialLoadComplete={this._initialLoadComplete}>
                            {(props: { initialLoadComplete: boolean }) =>
                                props.initialLoadComplete ? (
                                    this.data && this.data.userHasReadAccess && this._permissionsContext ? (
                                        <WrappedComponent
                                            dependencies={["ms.vss-admin-web.security-members-common-content"]}
                                            wrappedType="ms.vss-admin-web.security-members-common"
                                            permissionsContext={this._permissionsContext}
                                            onShowMessageCallout={this._showMessageCard}
                                            onLogTelemetry={this._logTelemetry}
                                            performanceService={this._performanceService}
                                        />
                                    ) : (
                                            <MessageCard className="message-card" severity={MessageCardSeverity.Error}>
                                                <span dangerouslySetInnerHTML={createMarkup(Resources.InvalidPermissionToViewObject)} />
                                            </MessageCard>
                                        )
                                ) : (
                                        <span>
                                            <Spinner size={SpinnerSize.large} className="spinner" />
                                        </span>
                                    )
                            }
                        </Observer>
                    </div>
                </Card>
            </div>
        );
    }

    private _getSecurityViewData = async () => {
        const telemetryLogger = logTelemetry(
            this.context.pageContext,
            this.props.permissionSetId,
            ResourceSecurityComponent.SecurityPanel,
            "Loading Security Data Model for Initialization."
        );
        this._performanceService.startScenario(PerformanceScenarios.LoadSecurityModel, false);
        this.data = await getSecurityViewData(this.context.pageContext, this.props.permissionSetId, this.props.permissionSetToken);

        if (this.data) {
            // With Json it's unclear whether it's type is undefined or the it actually contains the string undefined
            if (typeof this.data.permissionsContextJson !== "undefined" && this.data.permissionsContextJson !== "undefined") {
                const permissionsContext: PermissionsContext = JSON.parse(this.data.permissionsContextJson) as PermissionsContext;
                this._permissionsContext = permissionsContext;
                this._permissionsContext.allowAADSearchInHosted = this.props.allowAADSearchInHosted || false;
            } else {
                this.data = undefined;
                telemetryLogger.captureError("getSecurityViewData: Undefined permissionsContextJson");
            }
        } else {
            telemetryLogger.captureError("getSecurityViewData: Data provider load failed. Check product trace.");
        }

        this._performanceService.endScenario(Feature.ResourceSecurity, PerformanceScenarios.LoadSecurityModel, false);
        this._initialLoadComplete.value = true;
    };

    private _showMessageCard = (severity: MessageCardSeverity, message: string) => {
        this._messageCardInfo.value = { severity: severity, message: message } as IMessageCardContent;
    };

    private _logTelemetry = (
        component: ResourceSecurityComponent,
        action: string,
        properties?: { [key: string]: any }
    ): AdminFeatureTelemetryLogger => {
        return logTelemetry(this.context.pageContext, this.props.permissionSetId, component, action, properties);
    };

    private _onDownloadReportClick = (): void => {
        this._createPermissionReport(this.props.resourceId!, this.props.resourceType, this.props.resourceName);
    };

    private _canDownloadReport(): boolean {
        return !!(
            this.props.resourceId &&
            this.props.resourceType &&
            this.data &&
            this.data.userIsCollectionAdmin &&
            this._user &&
            this._user.descriptor &&
            isFeatureFlagEnabled(this.context.pageContext, Constants.EnableExportPermissionsFF, false)
        );
    }

    private _createPermissionReport = (resourceId: string, resourceType: any, resourceName?: string) => {
        const telemetryLogger = logTelemetry(
            this.context.pageContext,
            this.props.permissionSetId,
            ResourceSecurityComponent.SecurityPanel,
            "Export permissions button clicked.",
            {
                user: this._user!.descriptor,
                resourceId: this.props.resourceId
            }
        );

        const reportName: string = this._getReportName();
        this._isExportPermissionsDisabled.value = true;

        createPermissionReport(this.context.pageContext, [], resourceId, resourceType, reportName, resourceName).then(
            async response => {
                sendToast(Resources.DownloadReportToast, this.context.pageContext);
                this._isExportPermissionsDisabled.value = false;
            },
            reason => {
                this._showMessageCard(MessageCardSeverity.Error, reason.message || String(reason));
                telemetryLogger.captureError(reason.message || String(reason));
                this._isExportPermissionsDisabled.value = false;
            }
        );
    };

    private _getReportName(): string {
        return `${this._user!.displayName.replace(/\s+/g, "").toLocaleLowerCase()}-${new Date().getTime()}`;
    }
}

VssComponent.register("ms.vss-admin-web.security-page-view-component", SecurityPageView);
